import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";

// Icons
import { ArrowLeft, HelpCircle } from "lucide-react";

// Components
import Modal from "@/components/Modal";
import ToggleLanguage from "@/components/ToggleLanguage";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { web } from "@/config/System";
import useScreen from "@/hooks/useScreen";

// Swiper
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

// Assets
import LogoSNBP from "@/assets/logos/snbp.png";
import LogoUndiksha from "@/assets/logos/undiksha.png";

export default function GuestLayout() {
  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();
  const { isMobile } = useScreen();
  const [showPanduan, setShowPanduan] = useState(false);

  // Determine page type for UI hints
  // const isLogin = pathname.startsWith("/login");
  const isRegistrasi = pathname.startsWith("/registrasi");

  // Data Panduan
  const listVideoPanduanIndonesia = [
    {
      icon: LogoUndiksha,
      title: "Panduan Pendaftaran Kembali",
      link: "https://www.youtube.com/embed/I0U1GpZUz-I",
      description:
        "Penjaringan siswa secara nasional berdasarkan prestasi dengan nilai rapor.",
    },
    // {
    //   icon: "",
    //   title: "Panduan Pascasarjana",
    //   link: "/panduan/Pascasarjana.pdf",
    //   description:
    //     "Jalur penerimaan untuk program magister (S2) dan doktoral (S3).",
    // },
  ];

  const listVideoPanduanEnglish = [
    {
      icon: LogoSNBP,
      title: "SNBP Guide",
      link: "https://www.youtube.com/embed/I0U1GpZUz-I",
      description:
        "A national selection process based on students' academic records.",
    },
    // {
    //   icon: "",
    //   title: "Postgraduate Guide",
    //   link: "/panduan/Pascasarjana.pdf",
    //   description:
    //     "An admission path for Master's (S2) and Doctoral (S3) programs.",
    // },
  ];

  const listVideoPanduan =
    i18n.language === "id"
      ? listVideoPanduanIndonesia
      : listVideoPanduanEnglish;

  return (
    <main className="flex min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      {/* --- LEFT SECTION (Visual & Info) - Hidden on Mobile --- */}
      <section className="hidden lg:flex w-[45%] flex-col justify-between bg-slate-900 text-white p-8 xl:p-12 relative overflow-hidden">
        {/* Background Decorative Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-slate-900 opacity-90 z-0" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-[150px] opacity-20" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500 rounded-full blur-[100px] opacity-20" />

        <div className="relative z-10 flex flex-col h-full">
          {/* Header Left */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to={
                new URLSearchParams(window.location.search).get("tag") ===
                "international"
                  ? "/international-admission"
                  : new URLSearchParams(window.location.search).get("tag") ===
                      "student"
                    ? "/student-exchange"
                    : "/"
              }
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm border border-white/10">
                <ArrowLeft className="w-5 h-5" />
              </div>
              <span className="font-medium text-sm tracking-wide">Back</span>
            </Link>
          </div>

          {/* Main Content Left (Swiper) */}
          <div className="flex-1 flex flex-col justify-center max-w-lg mx-auto w-full">
            <div className="mb-8 space-y-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={web.logo} alt="Logo" className="h-12 w-auto" />
                <div className="h-8 w-[1px] bg-white/30" />
                <span className="font-bold text-lg tracking-wider uppercase">
                  {web.univAcronym}
                </span>
              </div>
              <h1 className="text-3xl font-bold leading-tight">
                {isRegistrasi
                  ? t("guest.layout.register.title")
                  : t("guest.layout.login.title")}
              </h1>
              <p className="text-slate-400">
                {t("guest.layout.register.description")}
              </p>
            </div>

            {/* Styled Swiper */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl">
              <Swiper
                spaceBetween={30}
                slidesPerView={1}
                pagination={{ clickable: true, dynamicBullets: true }}
                modules={[Autoplay, Pagination]}
                className="w-full pb-8"
              >
                {listVideoPanduan.map((video, index) => (
                  <SwiperSlide key={index}>
                    <div className="space-y-4">
                      <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-lg group">
                        {video.link.includes("youtube") ? (
                          <iframe
                            src={video.link}
                            className="w-full h-full"
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full bg-slate-800 text-slate-400">
                            PDF Document Preview
                          </div>
                        )}
                      </div>
                      <div className="px-1">
                        <h3 className="font-semibold text-lg text-white">
                          {video.title}
                        </h3>
                        <p className="text-sm text-slate-300 line-clamp-2 leading-relaxed">
                          {video.description}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>

          {/* Footer Left */}
          <div className="mt-8 text-xs text-slate-500 text-center">
            &copy; {new Date().getFullYear()} {web.univ}. All rights reserved.
          </div>
        </div>
      </section>

      {/* --- RIGHT SECTION (Outlet / Form) --- */}
      <section className="flex-1 flex flex-col relative bg-white dark:bg-slate-900 lg:bg-slate-50 lg:dark:bg-slate-950 transition-colors duration-300">
        {/* Top Bar Mobile/Desktop */}
        <div className="absolute top-0 right-0 p-6 z-20">
          <ToggleLanguage />
        </div>

        {/* Form Container */}
        <ScrollArea className="flex-1">
          <div className="flex min-h-screen items-center justify-center p-4 sm:p-8 lg:p-12">
            <div className="w-full max-w-md space-y-8 bg-white dark:bg-slate-900 lg:p-10 lg:rounded-3xl lg:shadow-xl lg:border border-slate-100 dark:border-slate-800 transition-colors duration-300">
              {/* Mobile Header (Only visible on mobile) */}
              <div className="lg:hidden flex flex-col items-center text-center space-y-4 mb-8">
                <Link to="/">
                  <img src={web.logo} alt="Logo" className="h-16 w-auto" />
                </Link>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {web.univAcronym}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Penerimaan Mahasiswa Baru
                  </p>
                </div>
              </div>

              {/* Render Login/Register Form */}
              <Outlet />
            </div>
          </div>
        </ScrollArea>
      </section>

      {/* --- MOBILE FLOATING BUTTON --- */}
      {isMobile && (
        <>
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              size="icon"
              className="h-12 w-12 rounded-full shadow-lg bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 dark:hover:bg-slate-700 text-white"
              onClick={() => setShowPanduan(true)}
            >
              <HelpCircle className="w-6 h-6" />
            </Button>
          </div>

          {/* Mobile Modal Panduan */}
          <Modal
            show={showPanduan}
            onClose={() => setShowPanduan(false)}
            className="dark:bg-slate-900"
          >
            <div className="p-4">
              <h3 className="font-bold text-lg mb-4 text-center dark:text-white">
                Panduan Pendaftaran
              </h3>
              <Swiper
                spaceBetween={20}
                slidesPerView={1}
                pagination={{ clickable: true }}
                modules={[Pagination]}
                className="pb-10"
              >
                {listVideoPanduan.map((video, index) => (
                  <SwiperSlide key={index}>
                    <div className="space-y-3">
                      <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                        <iframe
                          src={video.link}
                          className="w-full h-full"
                          allowFullScreen
                        />
                      </div>
                      <div>
                        <h4 className="font-bold dark:text-white">
                          {video.title}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {video.description}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </Modal>
        </>
      )}
    </main>
  );
}
